import axiosInstance from './axios';
import { QBOrderItemDetail, QBOrderDetail } from './customers';

export type { QBOrderItemDetail, QBOrderDetail };

export interface BubbleTeaMenuItem {
  menu_item_id: number;
  name: string;
  name_zh?: string | null;
  description: string | null;
  description_zh?: string | null;
  price: number;
  image_url: string | null;
  available: boolean;
}

export interface BubbleTeaMenuItemResponse {
  menu_item_id: number;
  name: string;
  name_zh?: string | null;
  description: string | null;
  description_zh?: string | null;
  price: number;
  image_url: string | null;
  available: boolean;
  menu_category: string | null;
}

export interface InstantCakeItemResponse {
  id: number;
  name: string;
  name_zh?: string | null;
  description: string | null;
  description_zh?: string | null;
  price: number;
  image_url: string | null;
  stock_count: number;
  active: boolean;
  category: string | null;
}

export interface QuickBitesMenuResponse {
  bubble_tea: BubbleTeaMenuItemResponse[];
  instant_cakes: InstantCakeItemResponse[];
}

export interface QuickBitesCheckoutRequest {
  customer_name: string;
  customer_phone: string;
  items: Array<{
    type: 'bubble_tea' | 'instant_cake';
    item_id: number;
    quantity: number;
    options?: Record<string, string>;
  }>;
  stripe_payment_method_id?: string;
  special_instructions?: string;
  // Mirrors backend/app/schemas/bubbletea.py's BubbleTeaOrderCreate.redeem_points --
  // customer opt-in to redeem their points balance against this QB order.
  redeem_points?: boolean;
  // Wallet only: total (pence) shown on the wallet sheet; the server aborts with
  // 409 total_changed before charging if its recomputed total differs by > 1p.
  expected_total_pence?: number;
  // UUID of one checkout attempt; lets the server derive a Stripe idempotency key that
  // survives a re-click (new PaymentMethod). Omitted when absent (legacy behaviour).
  checkout_attempt_id?: string;
  checkout_attempt_secret?: string;
}

export interface QuickBitesCheckoutResponse {
  queue_number: number;
  order_id: number;
  total: number;
  items?: Array<{
    bt_order_item_id: number;
    menu_item_id: number;
    quantity: number;
    unit_price: number;
    subtotal: number;
    options?: Record<string, string> | null;
  }>;
  // Optional: absent on older responses; treated as 0 by QBCheckout.
  points_redeemed_value?: number;
}

export interface BubbleTeaOrderCreate {
  items: { menu_item_id: number; quantity: number; options?: Record<string, string> }[];
  customer_name?: string;
  customer_phone?: string;
  // Mirrors backend/app/schemas/bubbletea.py's BubbleTeaOrderCreate.redeem_points --
  // customer opt-in to redeem their points balance against this QB order.
  redeem_points?: boolean;
}

export interface BubbleTeaOrderResponse {
  bt_order_id: number;
  ordering_qr_token: string;
  pickup_qr_token: string;
  status: string;
  total: number;
  customer_name: string | null;
  customer_phone: string | null;
  items: {
    bt_order_item_id: number;
    menu_item_id: number;
    quantity: number;
    unit_price: number;
    subtotal: number;
  }[];
  // Mirrors backend/app/schemas/bubbletea.py's BubbleTeaOrderResponse.points_earned/
  // points_redeemed/points_redeemed_value -- always populated (non-nullable,
  // default 0/0/0.0) once a BubbleTeaOrder row exists.
  points_earned: number;
  points_redeemed: number;
  points_redeemed_value: number;
  // Lifetime-discount snapshot (0/absent for guests and 0% customers).
  lifetime_discount_percent?: number;
  lifetime_discount_amount?: number;
}

export const bubbleTeaAPI = {
  getMenu: async (): Promise<BubbleTeaMenuItem[]> => {
    const response = await axiosInstance.get('/bubble-tea/menu');
    return response.data;
  },

  createOrder: async (data: BubbleTeaOrderCreate): Promise<BubbleTeaOrderResponse> => {
    const response = await axiosInstance.post('/bubble-tea/orders', data);
    return response.data;
  },

  getOrderByPickupToken: async (token: string): Promise<BubbleTeaOrderResponse> => {
    const response = await axiosInstance.get(`/bubble-tea/orders/${token}`);
    return response.data;
  },

  /** Returns the URL of the shop QR code image (PNG) that directs customers to the ordering page. */
  getMenuQrImageUrl: (): string => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    return `${base}/bubble-tea/menu/qr-image`;
  },
};

export const fetchQuickBitesMenu = async (): Promise<QuickBitesMenuResponse> => {
  const response = await axiosInstance.get('/quick-bites/menu');
  const data = response.data;
  return {
    bubble_tea: Array.isArray(data.bubble_tea) ? data.bubble_tea : [],
    instant_cakes: Array.isArray(data.instant_cakes) ? data.instant_cakes : [],
  };
};

export const lookupOrders = async (phone: string, queueNumber: number): Promise<QBOrderDetail[]> => {
  const response = await axiosInstance.get('/bubble-tea/orders/lookup', {
    params: { phone, queue_number: queueNumber },
  });
  return response.data;
};

export const createQuickBitesCheckout = async (data: QuickBitesCheckoutRequest): Promise<QuickBitesCheckoutResponse> => {
  const bubbleTeaItems = data.items
    .filter(i => i.type === 'bubble_tea')
    .map(i => ({ menu_item_id: i.item_id, quantity: i.quantity, ...(i.options && Object.keys(i.options).length > 0 ? { options: i.options } : {}) }));
  const instantCakeItems = data.items
    .filter(i => i.type === 'instant_cake')
    .map(i => ({ instant_cake_item_id: i.item_id, quantity: i.quantity }));

  const payload: Record<string, unknown> = {
    customer_name: data.customer_name,
    customer_phone: data.customer_phone,
    items: bubbleTeaItems,
    ...(instantCakeItems.length > 0 ? { instant_cake_items: instantCakeItems } : {}),
    ...(data.stripe_payment_method_id ? { stripe_payment_method_id: data.stripe_payment_method_id } : {}),
    ...(data.special_instructions ? { special_instructions: data.special_instructions } : {}),
    ...(data.redeem_points ? { redeem_points: true } : {}),
    ...(typeof data.expected_total_pence === 'number' ? { expected_total_pence: data.expected_total_pence } : {}),
    ...(data.checkout_attempt_id ? { checkout_attempt_id: data.checkout_attempt_id } : {}),
    ...(data.checkout_attempt_id && data.checkout_attempt_secret
      ? { checkout_attempt_secret: data.checkout_attempt_secret }
      : {}),
  };

  const response = await axiosInstance.post('/bubble-tea/orders', payload);
  return response.data;
};
